import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditGoogleClassroom } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "../../action-doc-builder"
import { partial } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  oldGoogleClassSettings: any
  googleCardId: string
  updatedClassSettings: any
  prop: string
}

export default function prepareGCSettingsUpdate(payload: Payload): PreparedAction {
  return {
    name: "GC_SETTINGS_UPDATE",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.googleCardId,
  })
    .set("embedded", `attributes.classSettings`, payload.updatedClassSettings)
    .inc("embedded", `attributes._revision`, 1).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let newEvent: LessonEditGoogleClassroom = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "google_classroom",

    adverbial_verb: "editing",
    adverbial_object_type: "card",
    adverbial_object_specifier: "gc_time",
    adverbial_object_id: payload.googleCardId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }

  return {
    title: `Changed GC Post ${payload.prop}`,
    titleForAnalytics: "Lesson Card - GC Post - Set Time",
    description: `Updated ${payload.prop} in GC class settings`,
    context: defaultLessonContext(payload, models),
    activity: defaultLessonActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
